import React from 'react';
import { t } from '@/locales/strings';

/**
 * CoachCard — entry tile for the method journey on the home screen.
 * Tapping it opens /method, where every phase and lesson is listed.
 * Text-first with a small rose glyph on the left; no portrait.
 *
 *   <CoachCard lang={lang} onTap={() => navigate('/method')} />
 */
export default function CoachCard({ lang = 'en', onTap }) {
  const Card = onTap ? 'button' : 'div';
  return (
    <Card
      type={onTap ? 'button' : undefined}
      onClick={onTap}
      aria-label={onTap ? t('coach_card_tap_aria', lang) : undefined}
      style={{
        appearance: 'none',
        display: 'grid',
        gridTemplateColumns: onTap ? '28px 1fr 14px' : '28px 1fr',
        gap: 12,
        alignItems: 'center',
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px',
        margin: '0 0 10px',
        background: 'var(--surface, #fff)',
        border: '1px solid color-mix(in srgb, var(--primary, #c97a68) 18%, transparent)',
        borderRadius: 18,
        boxShadow: '0 1px 2px rgba(45,31,38,.05), 0 10px 24px -16px rgba(45,31,38,.18)',
        cursor: onTap ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <CoachMark />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <div style={{
          font: '600 10px/1 var(--font-ui, Manrope)',
          letterSpacing: '0.26em',
          textTransform: 'uppercase',
          color: 'var(--ink-mute, #9c8590)',
        }}>
          {t('coach_eyebrow', lang)}
        </div>
        <div style={{
          font: '500 15px/1.3 var(--font-title, Fraunces), serif',
          color: 'var(--ink, #2d1f26)',
          textWrap: 'balance',
          letterSpacing: '-0.005em',
        }}>
          {t('coach_card_headline', lang)}
        </div>
      </div>
      {onTap && (
        <svg
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round"
          aria-hidden="true"
          style={{
            color: 'var(--primary, #c97a68)',
            alignSelf: 'center',
            flex: '0 0 auto',
            opacity: 0.7,
          }}
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      )}
    </Card>
  );
}

// CoachMark — a small stylised rose glyph that stands in for a
// portrait. Uses the same rose palette as the rest of the player so
// the card feels like part of the method, not a foreign visitor.
function CoachMark() {
  return (
    <svg
      width="28"
      height="34"
      viewBox="0 0 36 44"
      aria-hidden="true"
      style={{ display: 'block', flex: '0 0 auto' }}
    >
      <defs>
        <radialGradient id="ccRoseG" cx="50%" cy="45%" r="60%">
          <stop offset="0%"  stopColor="#fbd6cb" />
          <stop offset="55%" stopColor="var(--primary-light, #e3a193)" />
          <stop offset="100%" stopColor="var(--primary, #c97a68)" />
        </radialGradient>
      </defs>
      {/* Stem */}
      <path d="M 18 44 L 18 22" stroke="var(--primary, #c97a68)" strokeWidth="1.3" strokeLinecap="round" opacity="0.55" />
      {/* Leaf */}
      <path d="M 18 30 Q 11 28 9 24 Q 13 26 18 26" fill="none" stroke="var(--primary, #c97a68)" strokeWidth="1.2" strokeLinecap="round" opacity="0.55" />
      {/* Rose petals */}
      <g transform="translate(18 14)">
        <ellipse cx="0" cy="-6" rx="3.2" ry="6.2" transform="rotate(0)"   fill="url(#ccRoseG)" opacity="0.9" />
        <ellipse cx="0" cy="-6" rx="3.2" ry="6.2" transform="rotate(60)"  fill="url(#ccRoseG)" opacity="0.9" />
        <ellipse cx="0" cy="-6" rx="3.2" ry="6.2" transform="rotate(120)" fill="url(#ccRoseG)" opacity="0.9" />
        <ellipse cx="0" cy="-6" rx="3.2" ry="6.2" transform="rotate(180)" fill="url(#ccRoseG)" opacity="0.9" />
        <ellipse cx="0" cy="-6" rx="3.2" ry="6.2" transform="rotate(240)" fill="url(#ccRoseG)" opacity="0.9" />
        <ellipse cx="0" cy="-6" rx="3.2" ry="6.2" transform="rotate(300)" fill="url(#ccRoseG)" opacity="0.9" />
      </g>
      <circle cx="18" cy="14" r="2" fill="var(--primary-dark, #a85c4b)" />
    </svg>
  );
}
